import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type SakhtarIconName } from '@sakhtar/design-system/react';
import './hub.css';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — module overview KPI row
 * (docs/design-system/reference/Sakhtar Cloud ERP v2.dc.html, every
 * `/t/kimia/<module>` frame). One shared implementation for every module hub
 * so the hero tile / tinted tile pair is identical across modules. Color and
 * pattern come from the nearest `[data-module]` (ModuleLayout sets it; the
 * grid's `accent` sets it for pages outside a module layout).
 */

export type HubAccent = 'brand' | 'crm' | 'inventory' | 'procurement' | 'finance' | 'projects';

export function HubKpiGrid({
  accent,
  children,
}: {
  readonly accent?: HubAccent | undefined;
  readonly children: ReactNode;
}) {
  return (
    <div
      className="hub-kpis"
      data-module={accent === undefined ? undefined : accent === 'brand' ? '' : accent}
    >
      {children}
    </div>
  );
}

export interface HubKpiProgress {
  /** 0–100. */
  readonly percent: number;
  readonly tone?: 'accent' | 'warn' | 'danger' | undefined;
}

export interface HubKpiTicks {
  readonly total: number;
  /** How many of `total` are highlighted (from the start). */
  readonly hot: number;
  /** Highlight color; danger by default. */
  readonly tone?: 'danger' | 'warn' | undefined;
}

export function HubKpi({
  label,
  value,
  scale,
  icon,
  href,
  delta,
  deltaDirection,
  hero = false,
  tone,
  weights,
  foot,
  footEnd,
  footTone,
  progress,
  ticks,
}: {
  readonly label: ReactNode;
  readonly value: ReactNode;
  /** Unit beside the value, e.g. «میلیون ریال». */
  readonly scale?: ReactNode | undefined;
  /** Icon chip at the end of the label row. */
  readonly icon?: SakhtarIconName | undefined;
  /** Makes the whole tile a link (already tenant-prefixed). */
  readonly href?: string | undefined;
  /** Legacy single foot line; prefer `foot`. */
  readonly delta?: ReactNode | undefined;
  readonly deltaDirection?: 'up' | 'down' | 'neutral' | undefined;
  /** Filled module-colored tile (first KPI of a hub). */
  readonly hero?: boolean | undefined;
  readonly tone?: 'danger' | undefined;
  /** Segmented strip under the value (relative sizes). */
  readonly weights?: readonly number[] | undefined;
  /** Foot text (start) and an optional trailing note (end). */
  readonly foot?: ReactNode | undefined;
  readonly footEnd?: ReactNode | undefined;
  readonly footTone?: 'up' | 'down' | 'danger' | undefined;
  readonly progress?: HubKpiProgress | undefined;
  readonly ticks?: HubKpiTicks | undefined;
}) {
  const total = (weights ?? []).reduce((a, b) => a + Math.max(b, 0), 0);
  const footText = foot ?? delta;
  const footClass =
    footTone ??
    (foot === undefined && deltaDirection && deltaDirection !== 'neutral'
      ? deltaDirection
      : undefined);
  const hasFoot =
    footText !== undefined ||
    footEnd !== undefined ||
    progress !== undefined ||
    ticks !== undefined ||
    (weights !== undefined && total > 0);

  const body = (
    <>
      <div className="hub-kpi__head">
        <span className="hub-kpi__label">{label}</span>
        {icon ? (
          <span className="hub-kpi__chip" aria-hidden="true">
            <Icon name={icon} size="sm" />
          </span>
        ) : null}
      </div>
      <div className={`hub-kpi__value sk-num${tone === 'danger' ? ' hub-kpi__value--danger' : ''}`}>
        <span>{value}</span>
        {scale ? <span className="hub-kpi__scale">{scale}</span> : null}
      </div>
      {hasFoot ? (
        <div className="hub-kpi__foot">
          {weights && total > 0 ? (
            <div className="hub-kpi__segs" aria-hidden="true">
              {weights.map((w, i) => (
                <i
                  key={i}
                  style={{
                    flex: Math.max(w / total, 0.02),
                    opacity: 0.32 + (0.68 * i) / Math.max(weights.length - 1, 1),
                  }}
                />
              ))}
            </div>
          ) : null}
          {footText !== undefined || footEnd !== undefined || ticks ? (
            <div
              className={`hub-kpi__foot-row${footClass ? ` hub-kpi__foot-row--${footClass}` : ''}`}
            >
              <span>{footText}</span>
              {ticks ? (
                <span
                  className={`hub-kpi__ticks${ticks.tone === 'warn' ? ' hub-kpi__ticks--warn' : ''}`}
                  aria-hidden="true"
                >
                  {Array.from({ length: Math.min(ticks.total, 12) }, (_, i) => (
                    <i key={i} className={i < ticks.hot ? 'is-hot' : undefined} />
                  ))}
                </span>
              ) : footEnd !== undefined ? (
                <span>{footEnd}</span>
              ) : null}
            </div>
          ) : null}
          {progress ? (
            <div
              className={`hub-kpi__bar${progress.tone && progress.tone !== 'accent' ? ` hub-kpi__bar--${progress.tone}` : ''}`}
              role="presentation"
            >
              <i style={{ width: `${Math.max(0, Math.min(100, progress.percent))}%` }} />
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );

  const className = `hub-kpi${hero ? ' hub-kpi--hero' : ''}`;
  return href ? (
    <Link to={href} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}
